import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"

// The reply composer: a formatting toolbar, a rich-text editor, the files picked for this
// reply, and Send — all inside one box.
//
// Sending really does reach Gmail and blocks for about a second, so the in-flight state is
// shown rather than left to look like a dead button. Files are uploaded the moment they are
// picked, which is what lets a rejected send hand them straight back instead of losing them.
export default class extends Controller {
  static targets = [
    "editor", "bodyField", "submit", "status", "hint", "format", "linkButton", "linkRow",
    "linkInput", "linkError", "files", "filesList", "filesTotal", "filesRejected",
    "fileTemplate", "fileInput", "attach", "signature"
  ]

  static values = { maxBytes: Number, directUploadUrl: String, autofocus: Boolean }

  // Only what the composer toolbar itself can produce, mirroring ReplyBodySanitizer on the
  // server. Anything pasted from outside is narrowed to this before it enters the editor.
  static PASTE_ALLOWED_TAGS = [ "p", "br", "strong", "b", "em", "i", "u", "a", "ol", "ul", "li" ]
  static PASTE_DROPPED_TAGS = [ "script", "style" ]
  static PASTE_ALLOWED_SCHEMES = [ "http", "https", "mailto" ]

  connect() {
    this.nameShortcutModifier()
    this.handleSelectionChange = this.handleSelectionChange.bind(this)
    document.addEventListener("selectionchange", this.handleSelectionChange)
    this.rejectedNames = []
    this.syncBody()
    this.refreshTotal()
    // Only the send response asks for focus. A thread the agent has merely opened renders the
    // composer resting, and focusing it here would expand every composer on every page load.
    if (this.autofocusValue) this.focusEditorEnd()
    this.syncExpanded()
  }

  disconnect() {
    document.removeEventListener("selectionchange", this.handleSelectionChange)
    // The caret timer moves the *document's* selection, so one left pending on a composer that
    // has been swapped out — every send replaces this element — would reach past its own detached
    // editor and clear the selection in the composer that replaced it.
    clearTimeout(this.caretTimer)
  }

  // The editor's selection is recorded on every change, not only on a toolbar mousedown —
  // activating the Link button from the keyboard fires click with no mousedown at all, and
  // without this the range would be stale by the time the URL field takes focus.
  handleSelectionChange() {
    this.saveSelection()
    this.refreshToolbar()
  }

  // maybeSubmit accepts either modifier, so the hint has to name whichever one the reader
  // actually has — and the key is labelled Return on a Mac keyboard but Enter on a PC one.
  // Rendered as the Mac wording server-side, since most of the team is on one, which keeps the
  // common case free of a visible swap.
  nameShortcutModifier() {
    if (!this.hasHintTarget) return

    const platform = navigator.userAgentData?.platform || navigator.platform || navigator.userAgent
    const isApple = /mac|iphone|ipad|ipod/i.test(platform)
    this.hintTarget.textContent = isApple ? "⌘ + Return to send" : "Ctrl + Enter to send"
  }

  // A failed send re-renders the composer with the markup preserved; dropping the caret at the
  // start would put it in front of everything they already wrote.
  focusEditorEnd() {
    const range = document.createRange()
    range.selectNodeContents(this.editorTarget)
    range.collapse(false)
    const selection = window.getSelection()
    selection.removeAllRanges()
    selection.addRange(range)
    this.editorTarget.focus()
  }

  // ---- body ----

  // A contenteditable is not a form control and submits nothing of its own, so the hidden field
  // is the only thing the server ever sees.
  syncBody() {
    this.normalizeEmptyEditor()
    this.bodyFieldTarget.value = this.editorTarget.innerHTML
    this.update()
  }

  // Browsers leave a stray <br> or an empty <div> behind once you type and delete, and the
  // placeholder is drawn from :empty — without this it disappears the first time the box is
  // used and never comes back.
  normalizeEmptyEditor() {
    if (this.editorText !== "") return
    // A list the agent has just started carries no text yet but is absolutely not empty;
    // clearing it here would delete the thing they only just asked for.
    if (this.editorTarget.querySelector("ol, ul, a")) return

    this.editorTarget.innerHTML = ""
  }

  get editorText() {
    return this.editorTarget.textContent.replace(/ /g, " ").trim()
  }

  // The sign-off is put in the box for the agent rather than written by them, so on its own it is
  // not a reply: it must neither arm Send nor hold the composer open the way typed text does.
  hasBodyText() {
    if (this.editorText === "") return false

    return this.editorText !== this.signatureText
  }

  update() {
    this.submitTarget.disabled = !this.canSend()
    this.syncExpanded()
  }

  // ---- signature ----

  // Read back off the template rather than repeated as a string here, so the emptiness rule above
  // cannot drift away from the markup that is actually inserted.
  get signatureText() {
    if (!this.hasSignatureTarget) return ""

    return this.signatureTarget.content.textContent.replace(/ /g, " ").trim()
  }

  // The agent gets one sign-off per composer, on their way into the editor. Deleting it is an
  // edit like any other, so it is never put back — a fresh composer, after a send, starts over.
  signOff() {
    if (this.signedOff) return
    // Set before the guard below, not after it: a composer re-rendered around a rejected send
    // already holds a sign-off, and leaving the flag clear would let a later focus add a second.
    this.signedOff = true
    if (!this.hasSignatureTarget) return
    if (this.editorText !== "") return

    this.editorTarget.appendChild(this.signatureTarget.content.cloneNode(true))
    this.parkCaretInBlankLine()
    // The editor posts nothing of its own, so without this the agent could send a reply whose
    // sign-off never reached the hidden field — and update() is what arms Send and expands the box.
    this.syncBody()
  }

  // The caret belongs in the blank line the skeleton leaves for the reply.
  //
  // Deferred by a turn rather than set inline, because a click is what usually brings us here and
  // the browser places its own caret as the *default action* of that click — after our focus
  // handler has run. Setting the range synchronously would simply be overwritten, and since the
  // agent clicked an empty editor, the position they hit-tested carries no intent about content
  // that did not exist yet. A programmatic focus (the composer that comes back after a send) has
  // no default action to lose to and lands here just the same, one turn later.
  parkCaretInBlankLine() {
    this.caretTimer = setTimeout(() => {
      // Found by content rather than by position, because the blank line moves: it is the first
      // block when the skeleton is only a sign-off, and the second when a greeting opens it.
      const blank = Array.from(this.editorTarget.children)
        .find((block) => block.textContent.trim() === "")
      const target = blank || this.editorTarget.firstChild
      if (!target) return

      const range = document.createRange()
      range.setStart(target, 0)
      range.collapse(true)
      const selection = window.getSelection()
      selection.removeAllRanges()
      selection.addRange(range)
    }, 0)
  }

  // ---- resting / expanded ----

  // At rest the composer is one line. Everything that makes it a rich-text composer — the
  // toolbar, the action row, the shortcut hint — is collapsed until the agent is actually
  // composing, which is either while they are in it or while it is holding something.

  expand() {
    this.element.classList.add("is-expanded")
  }

  // relatedTarget is whatever is about to take focus; a null one means focus went somewhere we
  // cannot name — which includes the native file dialog the (+) button opens and an ordinary
  // tab switch, neither of which is the agent leaving the composer. document.hasFocus() is what
  // tells those apart from a real click elsewhere on the page.
  focusOut(event) {
    if (this.element.contains(event.relatedTarget)) return
    if (!document.hasFocus()) return

    this.syncExpanded()
  }

  syncExpanded() {
    this.element.classList.toggle("is-expanded", this.shouldExpand())
  }

  shouldExpand() {
    if (this.element.contains(document.activeElement)) return true
    // Collapsing mid-send would take the spinner and the disabled Send button off screen.
    if (this.element.classList.contains("is-sending")) return true

    return this.hasContent()
  }

  // Deliberately the same emptiness rule canSend() uses, plus three things that are not text:
  // a list or link the agent has only just started — which normalizeEmptyEditor keeps on purpose
  // — staged files, whose chips and running total exist nowhere else on the page, and an unread
  // over-cap rejection. That last one is the odd member: the composer is genuinely empty, but
  // collapsing would swallow the only explanation the agent gets for a file that has silently
  // vanished from their reply. It clears on their next pick, which is when the notice is stale.
  hasContent() {
    if (this.hasBodyText()) return true
    if (this.editorTarget.querySelector("ol, ul, a")) return true
    if (this.fileRows.length > 0) return true

    return !this.filesRejectedTarget.hidden
  }

  // A reply carrying only files is legitimate, so text is not required — but nothing may be
  // sent while an upload is still in flight or the payload is over the cap.
  canSend() {
    if (this.uploadingRows.length > 0) return false
    if (this.totalBytes > this.maxBytesValue) return false

    return this.hasBodyText() || this.readyRows.length > 0
  }

  maybeSubmit(event) {
    if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
      event.preventDefault()
      if (!this.submitTarget.disabled) this.element.requestSubmit()
    }
  }

  // ---- formatting ----

  // Fired on mousedown, before the click. Without stopping the default the button takes focus
  // and the editor's selection collapses, so the command would land on nothing.
  holdSelection(event) {
    event.preventDefault()
    this.saveSelection()
  }

  format(event) {
    const command = event.currentTarget.dataset.command
    this.editorTarget.focus()
    this.ensureEditableBlock()
    document.execCommand(command, false, null)
    this.syncBody()
    this.refreshToolbar()
  }

  // insertOrderedList / insertUnorderedList need a block to convert, and a genuinely empty
  // contenteditable has none — the command silently does nothing. Starting a list on an empty
  // composer is an ordinary thing to do, so give it a paragraph to work with and put the caret
  // inside it.
  ensureEditableBlock() {
    if (this.editorTarget.innerHTML !== "") return

    this.editorTarget.innerHTML = "<p><br></p>"
    const range = document.createRange()
    range.setStart(this.editorTarget.firstElementChild, 0)
    range.collapse(true)
    const selection = window.getSelection()
    selection.removeAllRanges()
    selection.addRange(range)
  }

  // Driven by selectionchange rather than by clicks, so moving the caret into bold text lights
  // the button too — the state belongs to where the caret is, not to what was last pressed.
  refreshToolbar() {
    if (!this.hasFormatTarget) return
    if (!this.editorTarget.contains(document.activeElement) &&
        document.activeElement !== this.editorTarget) return

    this.formatTargets.forEach((button) => {
      button.setAttribute("aria-pressed", String(this.commandActive(button.dataset.command)))
    })
  }

  // queryCommandState is deprecated along with execCommand and throws on some commands in some
  // engines. The pressed state is a nicety; losing it must never break typing.
  commandActive(command) {
    try {
      return document.queryCommandState(command)
    } catch {
      return false
    }
  }

  // ---- links ----

  toggleLink() {
    if (this.linkRowTarget.hidden) this.openLink()
    else this.cancelLink()
  }

  openLink() {
    // Captured here, from the live selection, rather than trusting whatever the last
    // selectionchange happened to record — that event is asynchronous, so on a keyboard
    // activation it may not have been delivered yet. At this instant nothing has taken focus
    // from the editor yet, so this is the last moment the real selection is readable.
    this.saveSelection()
    this.linkRowTarget.hidden = false
    this.linkButtonTarget.setAttribute("aria-expanded", "true")
    this.linkErrorTarget.hidden = true
    // Focusing the field is what destroys the editor's selection, so the range saved on
    // mousedown is the only record of what the agent had highlighted.
    this.linkInputTarget.value = this.currentLinkHref()
    this.linkInputTarget.focus()
    this.linkInputTarget.select()
  }

  // Editing a link the caret is already sitting in, rather than making a second one on top.
  currentLinkHref() {
    const anchor = this.savedRange?.commonAncestorContainer?.parentElement?.closest?.("a")

    return anchor?.getAttribute("href") || ""
  }

  applyLink() {
    const url = this.normalizedUrl(this.linkInputTarget.value)
    if (url === null) {
      this.linkErrorTarget.hidden = false
      this.linkInputTarget.classList.add("composer__link-input--invalid")
      return
    }

    this.restoreSelection()
    document.execCommand("createLink", false, url)
    this.closeLink()
    this.syncBody()
  }

  cancelLink() {
    this.closeLink()
    this.restoreSelection()
  }

  closeLink() {
    this.linkRowTarget.hidden = true
    this.linkErrorTarget.hidden = true
    this.linkInputTarget.classList.remove("composer__link-input--invalid")
    this.linkInputTarget.value = ""
    this.linkButtonTarget.setAttribute("aria-expanded", "false")
  }

  linkKeydown(event) {
    if (event.key === "Enter") {
      event.preventDefault()
      this.applyLink()
    } else if (event.key === "Escape") {
      event.preventDefault()
      this.cancelLink()
    }
  }

  // A bare "example.com" is what people type, so it is completed rather than refused. An
  // explicit scheme we do not accept is refused outright — silently rewriting javascript: into
  // https://javascript: would be worse than saying no. The server sanitizes regardless; this is
  // only here so the agent finds out before they send.
  normalizedUrl(value) {
    const url = value.trim()
    if (url === "") return null
    if (/^https?:\/\//i.test(url)) return url
    if (/^[a-z][a-z0-9+.-]*:/i.test(url)) return null

    return `https://${url}`
  }

  // ---- paste ----

  // A paste from a webpage carries that page's markup, styles and classes. Left alone it would
  // land in the editor looking right, then be stripped by the server on the way out — so the
  // agent sees one thing and the client receives another. Narrowed here to exactly what the
  // toolbar can produce, which is exactly what ReplyBodySanitizer allows.
  //
  // Chrome's paste-and-match-style (⌘⇧V) supplies no text/html at all, so the plain-text branch
  // is reached by that shortcut without needing to inspect a modifier the event does not carry.
  paste(event) {
    const clipboard = event.clipboardData
    if (!clipboard) return

    event.preventDefault()
    const html = clipboard.getData("text/html")

    if (html) this.insertFragment(this.sanitizedFragment(html))
    else document.execCommand("insertText", false, clipboard.getData("text/plain"))

    this.syncBody()
  }

  // Parsed into an inert document rather than assembled from strings, and rebuilt node by node
  // so nothing from the source survives except the tags and the one attribute we allow.
  sanitizedFragment(html) {
    const parsed = new DOMParser().parseFromString(html, "text/html")
    const fragment = document.createDocumentFragment()

    Array.from(parsed.body.childNodes).forEach((node) => {
      const clean = this.sanitizedNode(node)
      if (clean) fragment.appendChild(clean)
    })

    return fragment
  }

  sanitizedNode(node) {
    if (node.nodeType === Node.TEXT_NODE) return document.createTextNode(node.nodeValue)
    if (node.nodeType !== Node.ELEMENT_NODE) return null

    const tag = node.tagName.toLowerCase()
    // Dropped with their contents — an allowlist that keeps the text would paste the source of
    // a script in as visible gibberish.
    if (this.constructor.PASTE_DROPPED_TAGS.includes(tag)) return null

    const children = Array.from(node.childNodes)
                          .map((child) => this.sanitizedNode(child))
                          .filter(Boolean)

    if (!this.constructor.PASTE_ALLOWED_TAGS.includes(tag)) return this.unwrapped(children)
    if (tag === "a" && !this.permittedHref(node.getAttribute("href"))) return this.unwrapped(children)

    const element = document.createElement(tag)
    if (tag === "a") element.setAttribute("href", this.permittedHref(node.getAttribute("href")))
    children.forEach((child) => element.appendChild(child))

    return element
  }

  // The element goes, its words stay — silently eating the text of a pasted heading would lose
  // part of what the agent meant to send.
  unwrapped(children) {
    const fragment = document.createDocumentFragment()
    children.forEach((child) => fragment.appendChild(child))

    return fragment
  }

  // Mirrors ReplyBodySanitizer#permitted_href?, including the normalization: control characters
  // and backslashes are stripped before the scheme is read, because a browser strips them too.
  permittedHref(value) {
    const href = String(value || "").replace(/[\u0000-\u0020]/g, "").replace(/\\/g, "/")
    if (href === "") return null
    if (/^\/{2,}/.test(href)) return null

    const scheme = href.match(/^([a-z][a-z0-9+.-]*):/i)
    if (!scheme) return href

    return this.constructor.PASTE_ALLOWED_SCHEMES.includes(scheme[1].toLowerCase()) ? href : null
  }

  insertFragment(fragment) {
    const selection = window.getSelection()
    if (selection.rangeCount === 0) return

    const range = selection.getRangeAt(0)
    if (!this.editorTarget.contains(range.commonAncestorContainer)) return

    // Captured before insertion: appending a fragment empties it, so afterwards there is
    // nothing left to measure the caret against.
    const lastNode = fragment.lastChild
    range.deleteContents()
    range.insertNode(fragment)
    if (!lastNode) return

    const caret = document.createRange()
    caret.setStartAfter(lastNode)
    caret.collapse(true)
    selection.removeAllRanges()
    selection.addRange(caret)
  }

  saveSelection() {
    // Never while the link row is open. Focusing the URL field collapses the editor's selection
    // and still reports it as living inside the editor, so recording it here would overwrite the
    // highlighted range with a collapsed one — the exact loss the saved range exists to prevent.
    if (this.hasLinkRowTarget && !this.linkRowTarget.hidden) return

    const selection = window.getSelection()
    if (selection.rangeCount === 0) return

    const range = selection.getRangeAt(0)
    if (!this.editorTarget.contains(range.commonAncestorContainer)) return

    this.savedRange = range.cloneRange()
  }

  restoreSelection() {
    this.editorTarget.focus()
    if (!this.savedRange) return

    const selection = window.getSelection()
    selection.removeAllRanges()
    selection.addRange(this.savedRange)
  }

  // ---- toolbar keyboard navigation ----

  // The toolbar is one tab stop; the arrows move within it. Tab from the editor would otherwise
  // walk the agent through six buttons before reaching Send.
  moveFocus(event) {
    const keys = { ArrowRight: 1, ArrowLeft: -1, Home: 0, End: 0 }
    if (!(event.key in keys)) return

    event.preventDefault()
    const buttons = this.toolbarButtons
    const current = buttons.indexOf(event.currentTarget)
    let next = current + keys[event.key]
    if (event.key === "Home") next = 0
    if (event.key === "End") next = buttons.length - 1
    if (next < 0) next = buttons.length - 1
    if (next >= buttons.length) next = 0

    buttons.forEach((button) => button.setAttribute("tabindex", "-1"))
    buttons[next].setAttribute("tabindex", "0")
    buttons[next].focus()
  }

  get toolbarButtons() {
    return Array.from(this.element.querySelectorAll(".composer__toolbar .composer__tool"))
  }

  // ---- attachments ----

  pickFiles() {
    this.fileInputTarget.click()
  }

  // Each pick starts a fresh verdict: a rejection from the previous batch has been read and
  // acted on, and leaving it up would accuse the agent of a file they already dealt with.
  filesPicked() {
    this.rejectedNames = []
    Array.from(this.fileInputTarget.files).forEach((file) => this.addFile(file))
    this.renderRejection()
    // Cleared so picking the same file again still fires a change event.
    this.fileInputTarget.value = ""
  }

  addFile(file) {
    if (this.totalBytes + file.size > this.maxBytesValue) {
      this.rejectedNames.push(file.name)
      return
    }

    const row = this.buildRow(file)
    this.filesListTarget.appendChild(row)
    this.refreshTotal()
    // Not optional: canSend() refuses while an upload is in flight, but that verdict only
    // reaches the button through update(). Without this the agent can send a reply whose
    // attachment has no signed id yet, and the file is silently left behind.
    this.update()
    this.upload(file, row)
  }

  renderRejection() {
    const names = this.rejectedNames
    const limit = this.humanSize(this.maxBytesValue)
    this.filesRejectedTarget.hidden = names.length === 0

    if (names.length === 1) {
      this.filesRejectedTarget.textContent = `${names[0]} is too large — ${limit} is the limit for one reply.`
    } else if (names.length > 1) {
      this.filesRejectedTarget.textContent =
        `${names.length} files are too large — ${limit} is the limit for one reply.`
    }

    this.syncFilesVisibility()
  }

  // Values only — the row's structure comes from the server-rendered template, so the chips a
  // failed send restores and the ones built here cannot drift apart.
  buildRow(file) {
    const row = this.fileTemplateTarget.content.firstElementChild.cloneNode(true)
    row.querySelector(".composer__file-name").textContent = file.name
    row.querySelector(".composer__file-size").textContent = this.humanSize(file.size)
    row.querySelector(".composer__file-remove").setAttribute("aria-label", `Remove ${file.name}`)
    row.dataset.byteSize = file.size
    row.classList.toggle("composer__file--image", file.type.startsWith("image/"))
    row.classList.add("is-uploading")
    row.querySelector(".composer__file-status").textContent = "Uploading…"

    return row
  }

  upload(file, row) {
    new DirectUpload(file, this.directUploadUrlValue).create((error, blob) => {
      if (error) {
        row.classList.remove("is-uploading")
        row.classList.add("is-failed")
        row.querySelector(".composer__file-status").textContent = "Couldn't upload — remove and try again."
      } else {
        row.classList.remove("is-uploading")
        row.querySelector(".composer__file-status").textContent = ""
        row.querySelector("input[type=hidden]").value = blob.signed_id
        row.dataset.signedId = blob.signed_id
      }
      this.refreshTotal()
      this.update()
    })
  }

  removeFile(event) {
    event.currentTarget.closest(".composer__file").remove()
    this.refreshTotal()
    this.update()
  }

  refreshTotal() {
    const rows = this.fileRows
    if (rows.length === 0) {
      this.filesTotalTarget.textContent = ""
      this.filesTotalTarget.classList.remove("composer__files-total--over")
    } else {
      const total = this.totalBytes
      const over = total > this.maxBytesValue
      const noun = rows.length === 1 ? "file" : "files"
      const cap = this.humanSize(this.maxBytesValue)
      this.filesTotalTarget.classList.toggle("composer__files-total--over", over)
      this.filesTotalTarget.textContent = over
        ? `${rows.length} ${noun} · ${this.humanSize(total)} of ${cap} — remove a file to send.`
        : `${rows.length} ${noun} · ${this.humanSize(total)} of ${cap}`
    }

    this.syncFilesVisibility()
  }

  // The strip carries both the list and the rejection notice, so it stays open while either has
  // something to say — a batch where every file was refused has no rows but very much has news.
  syncFilesVisibility() {
    this.filesTarget.hidden = this.fileRows.length === 0 && this.filesRejectedTarget.hidden
  }

  get fileRows() {
    return Array.from(this.filesListTarget.querySelectorAll(".composer__file"))
  }

  get uploadingRows() {
    return this.fileRows.filter((row) => row.classList.contains("is-uploading"))
  }

  get readyRows() {
    return this.fileRows.filter((row) => !row.classList.contains("is-uploading") &&
                                         !row.classList.contains("is-failed"))
  }

  get totalBytes() {
    return this.fileRows.reduce((sum, row) => sum + Number(row.dataset.byteSize || 0), 0)
  }

  // Matches the server's number_to_human_size closely enough that the running total and the
  // failure message an over-cap send returns do not read as two different numbers.
  humanSize(bytes) {
    if (bytes < 1024) return `${bytes} Bytes`
    const units = ["KB", "MB", "GB"]
    let size = bytes / 1024
    let unit = 0
    while (size >= 1024 && unit < units.length - 1) {
      size /= 1024
      unit += 1
    }

    return `${size < 10 ? size.toFixed(1) : Math.round(size)} ${units[unit]}`
  }

  // ---- in-flight ----

  sending() {
    this.syncBody()
    this.element.classList.add("is-sending")
    this.element.setAttribute("aria-busy", "true")
    this.submitTarget.disabled = true
    // There is no readonly on a contenteditable. The value is already mirrored into the hidden
    // field, so unlike the old textarea there is nothing here that not-submitting could lose.
    this.editorTarget.setAttribute("contenteditable", "false")
    this.setToolsDisabled(true)
    // Announced, not shown: the spinner that replaces the send glyph is the visual signal, and
    // it is aria-hidden, so this live region is what a screen reader actually gets.
    this.statusTarget.textContent = "Sending…"
  }

  // A successful or rejected send replaces this whole composer, so clearing the state is
  // usually moot. It is not moot when the request never lands — a dropped connection or a
  // Turbo fetch error ends the submission with nothing to swap in, and without this the
  // spinner would spin forever over an editor the agent can no longer type in.
  sent() {
    this.element.classList.remove("is-sending")
    this.element.removeAttribute("aria-busy")
    this.editorTarget.setAttribute("contenteditable", "true")
    this.setToolsDisabled(false)
    this.statusTarget.textContent = ""
    this.update()
  }

  setToolsDisabled(disabled) {
    this.toolbarButtons.forEach((button) => { button.disabled = disabled })
    this.attachTarget.disabled = disabled
  }
}
